var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { SignalWatcher } from '@lit-labs/signals';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { bucketLocatorState, createBucket, createLocation, createTag, deleteBucket, deleteLocation, deletePart, deleteTag, decrementPart, intakePart, loadBucketLocator, moveBucket, updatePart, updateBucket, updateLocation, updateTag, } from '@df/state';
import './bucket-locator-panels.js';
import './bucket-record-manager.js';
import './part-edit-dialog.js';
let BucketLocatorApp = class BucketLocatorApp extends SignalWatcher(LitElement) {
    constructor() {
        super();
        this.authChanged = (event) => {
            const detail = event.detail;
            if (detail?.newValue)
                void loadBucketLocator();
        };
        this.saveIntake = async (event) => {
            try {
                await intakePart(event.detail);
                this.renderRoot.querySelector('bucket-intake-panel')?.resetIntake();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.addLocation = async (event) => {
            const detail = event.detail;
            try {
                await createLocation(detail.name, detail.description);
                this.renderRoot.querySelector('bucket-storage-panel')?.resetLocation();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.addBucket = async (event) => {
            const detail = event.detail;
            try {
                this.assignedBucket = (await createBucket(detail.locationId, detail.description)).bucket_id;
                this.renderRoot.querySelector('bucket-storage-panel')?.resetBucket();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.relocateBucket = async (event) => {
            const detail = event.detail;
            try {
                await moveBucket(detail.bucketId, detail.locationId);
                this.renderRoot.querySelector('bucket-storage-panel')?.resetMove();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.editLocation = async (event) => {
            const detail = event.detail;
            try {
                await updateLocation(detail);
                this.renderRoot.querySelector('bucket-record-manager')?.resetLocationEdit();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.removeLocation = async (event) => {
            const detail = event.detail;
            if (!window.confirm(`Delete location "${detail.name}"?`))
                return;
            try {
                await deleteLocation(detail.id);
            }
            catch { /* The store publishes the request error. */ }
        };
        this.editBucket = async (event) => {
            const detail = event.detail;
            try {
                await updateBucket(detail);
                this.renderRoot.querySelector('bucket-record-manager')?.resetBucketEdit();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.removeBucket = async (event) => {
            const detail = event.detail;
            if (!window.confirm(`Delete empty bucket ${detail.id}?`))
                return;
            try {
                await deleteBucket(detail.id);
            }
            catch { /* The store publishes the request error. */ }
        };
        this.addTag = async (event) => {
            const detail = event.detail;
            try {
                await createTag(detail.label);
                this.renderRoot.querySelector('bucket-record-manager')?.resetTagEdit();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.editTag = async (event) => {
            const detail = event.detail;
            try {
                await updateTag(detail.id, detail.label);
                this.renderRoot.querySelector('bucket-record-manager')?.resetTagEdit();
            }
            catch { /* The store publishes the request error. */ }
        };
        this.removeTag = async (event) => {
            const detail = event.detail;
            const effect = detail.count ? ` This also removes the tag from ${detail.count} part(s).` : '';
            if (!window.confirm(`Delete tag "${detail.label}"?${effect}`))
                return;
            try {
                await deleteTag(detail.id);
            }
            catch { /* The store publishes the request error. */ }
        };
        this.takeOne = async (event) => {
            try {
                await decrementPart(event.detail.id);
            }
            catch { /* The store publishes the request error. */ }
        };
        this.removePart = async (event) => {
            const part = event.detail;
            if (!window.confirm(`Permanently delete "${part.name}" and its photo?`))
                return;
            try {
                await deletePart(part.id);
            }
            catch { /* The store publishes the request error. */ }
        };
        this.openEdit = (event) => {
            this.editingPart = event.detail;
            void this.updateComplete.then(() => this.renderRoot.querySelector('part-edit-dialog')?.show());
        };
        this.savePartEdit = async (event) => {
            const detail = event.detail;
            try {
                await updatePart(detail);
                this.renderRoot.querySelector('part-edit-dialog')?.closeDialog();
                this.editingPart = null;
            }
            catch { /* The store publishes the request error. */ }
        };
        this.assignedBucket = '';
        this.editingPart = null;
    }
    static { this.styles = css `
    :host { display: block; color: var(--md-sys-color-on-surface, #202824); }
    .page { max-width: 1440px; margin: 0 auto; padding: 24px clamp(16px, 3vw, 44px) 56px; }
    .masthead { display: flex; align-items: end; justify-content: space-between; gap: 18px; padding: 10px 0 24px; border-bottom: 1px solid #c9d0c8; }
    h1, p { margin-top: 0; }
    h1 { margin-bottom: 0; color: #174c3d; font: 500 clamp(2rem, 5vw, 3.4rem) Georgia, serif; }
    .eyebrow { margin-bottom: 8px; color: #9d442f; font-size: .75rem; font-weight: 700; text-transform: uppercase; }
    .identity { color: #4d5d54; font-size: .9rem; text-align: right; }
    .workspace { display: grid; grid-template-columns: minmax(300px, .8fr) minmax(0, 1.5fr); gap: clamp(22px, 4vw, 54px); padding-top: 26px; }
    .column { min-width: 0; }
    .error { margin: 12px 0; padding: 14px; border-left: 3px solid #a33e2b; background: #f8e9e5; color: #812f22; }
    @media (max-width: 820px) { .workspace { grid-template-columns: 1fr; } .masthead { align-items: start; flex-direction: column; } .identity { text-align: left; } }
    @media (max-width: 560px) { .page { padding-top: 14px; } }
  `; }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('df-standard-pioneer-auth-wrapper-user-changed', this.authChanged);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('df-standard-pioneer-auth-wrapper-user-changed', this.authChanged);
    }
    render() {
        const state = bucketLocatorState.get();
        return html `<main class="page">
      <header class="masthead">
        <div><p class="eyebrow">Storage index · ${state.buckets.length} buckets</p><h1>Bucket Locator</h1></div>
        <p class="identity">${state.loading ? 'Connecting to your store…' : `${state.locations.length} locations · ${state.parts.length} matches`}</p>
      </header>
      ${state.error ? html `<p class="error" role="alert">${state.error}</p>` : nothing}
      <div class="workspace">
        <div class="column">
          <bucket-intake-panel @df-bucket-locator-intake=${this.saveIntake}></bucket-intake-panel>
          <bucket-storage-panel .assignedBucket=${this.assignedBucket}
            @df-bucket-locator-location-create=${this.addLocation}
            @df-bucket-locator-bucket-create=${this.addBucket}
            @df-bucket-locator-bucket-move=${this.relocateBucket}></bucket-storage-panel>
          <bucket-record-manager
            @df-bucket-locator-location-update=${this.editLocation}
            @df-bucket-locator-location-delete=${this.removeLocation}
            @df-bucket-locator-bucket-update=${this.editBucket}
            @df-bucket-locator-bucket-delete=${this.removeBucket}
            @df-bucket-locator-tag-create=${this.addTag}
            @df-bucket-locator-tag-update=${this.editTag}
            @df-bucket-locator-tag-delete=${this.removeTag}></bucket-record-manager>
        </div>
        <bucket-search-panel @df-bucket-locator-part-take=${this.takeOne}
          @df-bucket-locator-part-edit-open=${this.openEdit}
          @df-bucket-locator-part-delete=${this.removePart}></bucket-search-panel>
      </div>
      <part-edit-dialog .currentPart=${this.editingPart}
        @df-bucket-locator-part-edit=${this.savePartEdit}
        @df-bucket-locator-edit-close=${() => { this.editingPart = null; }}></part-edit-dialog>
    </main>`;
    }
};
__decorate([
    state()
], BucketLocatorApp.prototype, "assignedBucket", void 0);
__decorate([
    state()
], BucketLocatorApp.prototype, "editingPart", void 0);
BucketLocatorApp = __decorate([
    customElement('bucket-locator-app')
], BucketLocatorApp);
export { BucketLocatorApp };
//# sourceMappingURL=bucket-locator-app.js.map